import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getComicById } from '@/mocks/comics';

export default function Article() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  const comic = useMemo(() => (id ? getComicById(id) : undefined), [id]);

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-24 h-24 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-emotion-sad-line text-4xl"></i>
            </div>
            <h2 className="font-heading text-xl text-foreground-700 mt-4">漫画未找到</h2>
            <p className="text-sm text-foreground-500 mt-2">该漫画不存在或已下架</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 mt-6 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 洞洞杂货铺漫画`,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: comic.chapters[0]?.updateDate || comic.updateTime,
    dateModified: comic.updateTime,
    publisher: {
      '@type': 'Organization',
      name: '36漫画网',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://36manhua.com/article/${comic.id}`,
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="bg-background-100 border-b border-background-200/70">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: `${comic.category}漫画`, href: `/category/${comic.categorySlug}` },
                  { label: comic.title },
                ]}
              />
            </div>
          </div>

          <div className="max-w-5xl mx-auto px-4 md:px-6 py-8">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-shrink-0">
                <div className="w-full lg:w-64 aspect-[3/4] rounded-lg overflow-hidden border border-background-200/70">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    title={`${comic.title} - 洞洞杂货铺漫画`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700">
                    {comic.category}
                  </span>
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    comic.status === '连载中'
                      ? 'bg-accent-500/15 text-accent-600'
                      : 'bg-foreground-200 text-foreground-600'
                  }`}>
                    {comic.status}
                  </span>
                  {comic.featured && (
                    <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-600">
                      编辑推荐
                    </span>
                  )}
                </div>

                <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-2">
                  {comic.title}
                </h1>
                <p className="text-sm text-foreground-500 mb-6">
                  作者：{comic.author}
                </p>

                <div className="flex flex-wrap items-center gap-4 md:gap-6 mb-6 p-4 rounded-lg bg-background-100">
                  <div className="text-center">
                    <p className="text-lg font-bold text-accent-500 flex items-center gap-1 justify-center">
                      <i className="ri-star-fill"></i>
                      {comic.rating}
                    </p>
                    <p className="text-xs text-foreground-500 mt-1">评分</p>
                  </div>
                  <div className="w-px h-10 bg-background-200"></div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-foreground-700">
                      {comic.views > 10000 ? `${(comic.views / 10000).toFixed(1)}万` : comic.views}
                    </p>
                    <p className="text-xs text-foreground-500 mt-1">阅读量</p>
                  </div>
                  <div className="w-px h-10 bg-background-200"></div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-foreground-700">{comic.chapters.length}话</p>
                    <p className="text-xs text-foreground-500 mt-1">章节数</p>
                  </div>
                  <div className="w-px h-10 bg-background-200"></div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-foreground-700">{comic.updateTime}</p>
                    <p className="text-xs text-foreground-500 mt-1">最近更新</p>
                  </div>
                </div>

                <div className="mb-6">
                  <h3 className="font-heading text-base text-foreground-800 mb-3">作品简介</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
                </div>

                <div className="flex flex-wrap gap-2 mb-8">
                  {comic.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full text-xs bg-background-100 text-foreground-600 border border-background-200/70"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Link
                    to={`/article/${comic.id}`}
                    className="flex items-center justify-center gap-2 px-8 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-play-circle-line"></i>
                    开始阅读第一话
                  </Link>
                  <button className="flex items-center justify-center gap-2 px-8 py-3 bg-background-100 text-foreground-700 rounded-full font-medium text-sm hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap border border-background-200/70">
                    <i className="ri-bookmark-line"></i>
                    加入收藏
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-12">
              <h2 className="font-heading text-xl text-foreground-900 mb-6 flex items-center gap-2">
                <i className="ri-file-list-3-line text-primary-500"></i>
                章节列表
                <span className="text-sm text-foreground-500 font-normal">（共{comic.chapters.length}话）</span>
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {comic.chapters.map((chapter) => (
                  <div
                    key={chapter.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-background-100 border border-background-200/70 hover:border-primary-300 transition-colors cursor-pointer group"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 text-xs font-medium flex-shrink-0 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                        {chapter.number}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground-800 truncate group-hover:text-primary-500 transition-colors">
                          {chapter.title}
                        </p>
                        <p className="text-xs text-foreground-400">{chapter.updateDate}</p>
                      </div>
                    </div>
                    <i className="ri-arrow-right-s-line text-foreground-400 group-hover:text-primary-500 transition-colors"></i>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}